import axios from 'axios';
import { Auth } from 'aws-amplify';
import { Chatbot, ChatbotUsage, Model, Prompt, KnowledgeBaseDocument, UsageStats, ApiResponse, AccountUsage } from '../types';
import config from './config';

const api = axios.create({
  baseURL: config.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate axios instance for KB API.
// Exported so services/webSources.ts can share the same auth interceptors
// rather than building a second client against the same base URL.
export const kbApi = axios.create({
  baseURL: config.KB_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptors to both API instances
const authRequestInterceptor = async (config: any) => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    config.headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    console.error('Error getting auth token:', error);
  }
  return config;
};

const authResponseInterceptor = {
  success: (response: any) => response,
  error: (error: any) => {
    if (error.response?.status === 401) {
      Auth.signOut();
    }
    return Promise.reject(error);
  }
};

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use(authResponseInterceptor.success, authResponseInterceptor.error);

kbApi.interceptors.request.use(authRequestInterceptor);
kbApi.interceptors.response.use(authResponseInterceptor.success, authResponseInterceptor.error);

// Create a separate axios instance for Admin API
const adminApiInstance = axios.create({
  baseURL: config.ADMIN_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminApiInstance.interceptors.request.use(authRequestInterceptor);
adminApiInstance.interceptors.response.use(authResponseInterceptor.success, authResponseInterceptor.error);

export const chatbotApi = {
  // Chatbot CRUD operations
  async getChatbots(): Promise<ApiResponse<Chatbot[]>> {
    const response = await api.get('/chatbots');
    return response.data;
  },

  async getChatbot(chatbotId: string): Promise<ApiResponse<Chatbot>> {
    const response = await api.get(`/chatbots/${chatbotId}`);
    return response.data;
  },

  async createChatbot(chatbot: Partial<Chatbot>): Promise<ApiResponse<Chatbot>> {
    console.log('Creating chatbot with data:', chatbot);
    const response = await api.post('/chatbots', chatbot);
    console.log('Create chatbot response:', response.data);
    return response.data;
  },

  async updateChatbot(chatbotId: string, updates: Partial<Chatbot>): Promise<ApiResponse<Chatbot>> {
    const response = await api.put(`/chatbots/${chatbotId}`, updates);
    return response.data;
  },

  async deleteChatbot(chatbotId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/chatbots/${chatbotId}`);
    return response.data;
  },

  // Usage statistics
  async getChatbotUsage(chatbotId: string): Promise<ApiResponse<ChatbotUsage>> {
    const response = await api.get(`/chatbots/${chatbotId}/usage`);
    return response.data;
  },

  async getUsageStats(chatbotId: string, period: string = '30d'): Promise<ApiResponse<UsageStats[]>> {
    const response = await api.get(`/chatbots/${chatbotId}/stats?period=${period}`);
    return response.data;
  },

  // Test chatbot
  async testChatbot(chatbotId: string, message: string): Promise<ApiResponse<{ response: string }>> {
    const response = await api.post(`/chatbots/${chatbotId}/test`, { message });
    return response.data;
  },

  // Knowledge base document management
  async getKnowledgeBaseDocuments(chatbotId: string): Promise<ApiResponse<{documents: KnowledgeBaseDocument[]}>> {
    const response = await kbApi.get(`/chatbots/${chatbotId}/knowledge-base/documents`);
    return response.data;
  },

  async getUploadUrl(chatbotId: string, filename: string, contentType: string, fileSize: number, replaceExisting?: boolean): Promise<ApiResponse<{
    uploadUrl: string;
    fields: Record<string, string>;
    documentId: string;
    expiresIn: number;
    replaced?: boolean;
  }>> {
    const requestData = {
      filename,
      contentType,
      fileSize,
      replaceExisting
    };
    
    try {
      const response = await kbApi.post(`/chatbots/${chatbotId}/knowledge-base/upload-url`, requestData);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  async checkFileConflict(chatbotId: string, filename: string, contentType: string, fileSize: number): Promise<{
    hasConflict: boolean;
    existingDocument?: any;
  }> {
    
    try {
      const result = await this.getUploadUrl(chatbotId, filename, contentType, fileSize, false);
      return { hasConflict: false };
    } catch (error: any) {
      
      if (error.response?.status === 409) {
        const responseData = error.response?.data;
        
        // Check if this is our expected conflict format
        if (responseData?.success === false && responseData?.data?.conflictType === 'filename_exists') {
          return {
            hasConflict: true,
            existingDocument: responseData.data.existingDocument
          };
        } else {
          // Even if format is unexpected, if we get 409, there's likely a conflict
          return {
            hasConflict: true,
            existingDocument: responseData?.data?.existingDocument || { 
              name: filename, 
              status: 'unknown',
              id: 'unknown'
            }
          };
        }
      }
      
      throw error;
    }
  },

  async confirmUpload(chatbotId: string, documentId: string): Promise<ApiResponse<void>> {
    const response = await kbApi.post(`/chatbots/${chatbotId}/knowledge-base/documents/${documentId}/confirm`);
    return response.data;
  },

  async cleanupFailedUpload(chatbotId: string, documentId: string): Promise<ApiResponse<void>> {
    const response = await kbApi.delete(`/chatbots/${chatbotId}/knowledge-base/documents/${documentId}/cleanup`);
    return response.data;
  },

  // XMLHttpRequest rather than fetch, because fetch cannot report upload
  // progress and the Knowledge tab shows a per-file bar. Success and CORS
  // handling are unchanged: 2xx passes, and an opaque CORS failure (status 0)
  // is still treated as success, since S3 answers 204 without CORS headers.
  async uploadDocumentToS3(
    uploadUrl: string,
    fields: Record<string, string>,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    const formData = new FormData();

    // Add all the presigned URL fields first
    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key]);
    });

    // Add the file last
    formData.append('file', file);

    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrl);

      xhr.upload.onprogress = (event) => {
        if (onProgress && event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        // Status 0 with no response is the CORS case the fetch version swallowed.
        console.log('Upload completed (ignoring CORS response error)');
        onProgress?.(100);
        resolve();
      };

      xhr.onabort = () => reject(new Error('Upload cancelled'));

      xhr.send(formData);
    });
  },

  async uploadDocument(
    chatbotId: string,
    file: File,
    replaceExisting?: boolean,
    onProgress?: (percent: number) => void
  ): Promise<ApiResponse<any>> {
    let documentId: string | undefined;

    try {
      // Step 1: Get presigned URL
      const uploadUrlResponse = await this.getUploadUrl(chatbotId, file.name, file.type, file.size, replaceExisting);
      if (!uploadUrlResponse.data) {
        throw new Error('Failed to get upload URL');
      }
      const { uploadUrl, fields } = uploadUrlResponse.data;
      documentId = uploadUrlResponse.data.documentId;

      // Step 2: Upload to S3
      await this.uploadDocumentToS3(uploadUrl, fields, file, onProgress);
      
      // Step 3: Confirm upload
      const confirmResponse = await this.confirmUpload(chatbotId, documentId);
      
      return confirmResponse;
      
    } catch (error) {
      // If we have a documentId and the error occurred after getting the presigned URL,
      // clean up the document record from DynamoDB
      if (documentId) {
        try {
          console.log('Upload failed, cleaning up document record:', documentId);
          await this.cleanupFailedUpload(chatbotId, documentId);
        } catch (cleanupError) {
          console.error('Failed to cleanup document record:', cleanupError);
          // Don't throw cleanup error, throw the original error
        }
      }
      throw error;
    }
  },

  async deleteDocument(chatbotId: string, documentId: string): Promise<ApiResponse<{
    message: string;
    triggerSync?: boolean;
  }>> {
    const response = await kbApi.delete(`/chatbots/${chatbotId}/knowledge-base/documents/${documentId}`);
    return response.data;
  },

  // Knowledge base sync management
  async startSync(chatbotId: string): Promise<ApiResponse<{
    jobId: string;
    status: string;
    documentsCount: number;
    message: string;
  }>> {
    const response = await kbApi.post(`/chatbots/${chatbotId}/knowledge-base/sync`);
    return response.data;
  },

  async getSyncStatus(chatbotId: string): Promise<ApiResponse<{
    status: string;
    jobId?: string;
    startTime?: string;
    endTime?: string;
    documentsCount: number;
    pendingDocuments: number;
    errorMessage?: string;
    canUpload: boolean;
  }>> {
    const response = await kbApi.get(`/chatbots/${chatbotId}/knowledge-base/sync/status`);
    return response.data;
  },

  // Web sources live in services/webSources.ts — one crawled site is one
  // Bedrock data source, not a document, so it does not belong on this object.

  // Chatbot-specific statistics
  async getChatbotStatistics(chatbotId: string, days: number = 7): Promise<ApiResponse<{
    messages_over_time: Array<{date: string, count: number}>;
    active_sessions: Array<{date: string, count: number}>;
    top_chatbots: Array<{chatbot_id: string, name: string, message_count: number}>;
    response_times: Array<{date: string, avg_response_time_ms: number}>;
    session_depths: Array<{bucket: string, count: number}>;
    platform_usage: Array<{platform: string, count: number}>;
  }>> {
    const response = await api.get(`/chatbots/${chatbotId}/statistics/dashboard?days=${days}`);
    return response.data;
  },

  async getChatbotSessions(chatbotId: string, limit: number = 50): Promise<ApiResponse<Array<{
    session_id: string;
    chatbot_id: string;
    platform: string;
    created_at: number;
    last_interaction: number;
    total_messages: number;
    duration_seconds: number;
    status: string;
  }>>> {
    const response = await api.get(`/chatbots/${chatbotId}/statistics/sessions?limit=${limit}`);
    return response.data;
  },

  async getChatbotSessionsPaginated(
    chatbotId: string, 
    page: number = 1, 
    limit: number = 20,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<{
    sessions: Array<{
      session_id: string;
      chatbot_id: string;
      platform: string;
      created_at: number;
      last_interaction: number;
      total_messages: number;
      duration_seconds: number;
      status: string;
    }>;
    pagination: {
      current_page: number;
      total_pages: number;
      total_sessions: number;
      per_page: number;
      has_next: boolean;
      has_prev: boolean;
    };
  }>> {
    let url = `/chatbots/${chatbotId}/statistics/sessions/paginated?page=${page}&limit=${limit}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    
    const response = await api.get(url);
    return response.data;
  },

  // Models and prompts
  async getModels(): Promise<ApiResponse<Model[]>> {
    const response = await api.get('/models');
    return response.data;
  },

  async getPrompts(): Promise<ApiResponse<Prompt[]>> {
    const response = await api.get('/prompts');
    return response.data;
  },

  async updatePrompt(promptId: number, promptText: string): Promise<ApiResponse<Prompt>> {
    const response = await api.put(`/prompts/${promptId}`, { promptText });
    return response.data;
  },
};

export const modelsApi = {
  async getModels(): Promise<ApiResponse<Model[]>> {
    const response = await api.get('/models');
    return response.data;
  },
};

export const userApi = {
  async getProfile(): Promise<ApiResponse<any>> {
    const response = await api.get('/user/profile');
    return response.data;
  },

  /**
   * Storage and chatbot usage against this account's plan limits.
   *
   * Reads the kb_usage ledger, never the RawDataSize metric — the metric is
   * stale between ingestions and only publishes for a knowledge base that
   * actually holds something.
   */
  async getUsage(): Promise<ApiResponse<AccountUsage>> {
    const response = await api.get('/user/usage');
    return response.data;
  },

  async getSubscription(): Promise<ApiResponse<any>> {
    const response = await api.get('/user/subscription');
    return response.data;
  },

  async createStripePortalSession(): Promise<ApiResponse<{ url: string }>> {
    const payload: any = {};
    
    // Include current URL for local development
    if (window.location.hostname === 'localhost') {
      payload.return_url = window.location.origin + '/subscription';
    }
    
    const response = await api.post('/user/stripe-portal', payload);
    return response.data;
  },

  async createCheckoutSession(priceId: string, mode: 'subscription' | 'payment'): Promise<ApiResponse<{ session_id: string; url: string }>> {
    const payload: any = {
      priceId,
      mode
    };
    
    // Include current URL for local development
    if (window.location.hostname === 'localhost') {
      payload.returnUrl = window.location.origin;
    }
    
    const response = await api.post('/user/create-checkout-session', payload);
    return response.data;
  },

  async deleteAccount(): Promise<ApiResponse<void>> {
    const response = await api.delete('/user/account');
    return response.data;
  },

  async updatePreferences(preferences: { theme?: 'light' | 'dark' | 'system' }): Promise<ApiResponse<any>> {
    const response = await api.patch('/user/preferences', preferences);
    return response.data;
  },
};

export const discordApi = {
  async getDiscordServers(): Promise<ApiResponse<{ servers: Array<{ id: string; name: string; icon: string | null; owner: boolean; permissions: number }>, count: number }>> {
    const response = await api.get('/discord/servers');
    return response.data;
  },

  async getDiscordIntegrations(): Promise<ApiResponse<{ integrations: Array<{ server_id: string; server_name: string; chatbot_id: string; status: string; created_at: string; updated_at: string }>, count: number }>> {
    const response = await api.get('/discord/integrations');
    return response.data;
  },

  async createDiscordIntegration(serverId: string, chatbotId: string, serverName: string): Promise<ApiResponse<{ server_id: string; server_name: string; chatbot_id: string }>> {
    const response = await api.post('/discord/integrations', {
      server_id: serverId,
      chatbot_id: chatbotId,
      server_name: serverName
    });
    return response.data;
  },

  async deleteDiscordIntegration(serverId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/discord/integrations/${serverId}`);
    return response.data;
  },

  async checkDiscordServerIntegration(serverId: string): Promise<ApiResponse<{
    exists: boolean;
    ownedByCurrentUser?: boolean;
    server_id: string;
    server_name?: string;
    chatbot_id?: string;
    chatbot_name?: string;
  }>> {
    const response = await api.get(`/discord/servers/${serverId}/integration`);
    return response.data;
  },

  async getDiscordLinkStatus(): Promise<ApiResponse<{
    linked: boolean;
    discord_user_id: string | null;
    discord_username: string | null;
    method: 'federated' | 'linked' | 'none';
    identity_provider: string;
    token_valid: boolean;
  }>> {
    const response = await api.get('/discord/link');
    return response.data;
  },

  async linkDiscordAccount(code: string, redirectUri: string): Promise<ApiResponse<{
    discord_user_id: string;
    discord_username: string;
  }>> {
    const response = await api.post('/discord/link', { code, redirect_uri: redirectUri });
    return response.data;
  },

  async unlinkDiscordAccount(): Promise<ApiResponse<void>> {
    const response = await api.delete('/discord/link');
    return response.data;
  },
};

export interface WordPressIntegration {
  site_id: string;
  user_id: string;
  chatbot_id: string;
  site_url: string;
  site_name: string;
  created_at: string;
  updated_at: string;
}

export const wordpressApi = {
  async getWordPressIntegrations(): Promise<ApiResponse<{ integrations: Array<WordPressIntegration> }>> {
    const response = await api.get('/wordpress/integrations');
    return response.data;
  },

  async createWordPressIntegration(
    siteId: string, 
    chatbotId: string, 
    siteUrl: string, 
    siteName: string
  ): Promise<ApiResponse<{ integration: WordPressIntegration }>> {
    const response = await api.post('/wordpress/integrations', {
      site_id: siteId,
      chatbot_id: chatbotId,
      site_url: siteUrl,
      site_name: siteName
    });
    return response.data;
  },

  async deleteWordPressIntegration(siteId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/wordpress/integrations/${siteId}`);
    return response.data;
  },

  async getChatbotWordPressIntegrations(chatbotId: string): Promise<ApiResponse<{ integrations: Array<WordPressIntegration> }>> {
    const response = await api.get(`/wordpress/integrations/chatbot/${chatbotId}`);
    return response.data;
  },

  async validateIntegrationCode(
    integrationCode: string,
    siteId: string,
    siteUrl: string,
    siteName: string
  ): Promise<ApiResponse<{ integration: WordPressIntegration }>> {
    const response = await api.post('/wordpress/integrations/validate-code', {
      integration_code: integrationCode,
      site_id: siteId,
      site_url: siteUrl,
      site_name: siteName
    });
    return response.data;
  },

  async getChatbotIntegrationCode(chatbotId: string): Promise<ApiResponse<{ code: string }>> {
    const response = await api.get(`/wordpress/integrations/chatbot/${chatbotId}/code`);
    return response.data;
  },

  async storeAutoIntegrationCode(integrationCode: string, chatbotId: string): Promise<ApiResponse<{ success: boolean; code: string }>> {
    const response = await api.post('/wordpress/integrations/store-auto-code', {
      integration_code: integrationCode,
      chatbot_id: chatbotId
    });
    return response.data;
  },
};

// Create a separate axios instance for converse API (no auth required)
const converseApiInstance = axios.create({
  baseURL: config.CONVERSE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Converse API for chat functionality
export const converseApi = {
  async sendMessage(chatbotId: string, message: string, sessionId?: string, platform: string = 'web'): Promise<ApiResponse<{
    response: string;
    session_id: string;
    conversation_id: string;
    usage: {
      tokens_used: number;
      credits_consumed: number;
    };
  }>> {
    const response = await converseApiInstance.post('/converse', {
      chatbot_id: chatbotId,
      message,
      session_id: sessionId,
      context: {
        platform
      }
    });
    return response.data;
  }
};

// Pricing API
export const pricingApi = {
  async getPricing(): Promise<ApiResponse<{
    subscriptions: Array<{
      id: string;
      stripePriceId: string;
      name: string;
      description?: string;
      price: number;
      currency: string;
      interval: string;
      credits?: number;
      features?: string[];
      recommended?: boolean;
    }>;
    creditPackages: Array<{
      id: string;
      stripePriceId: string;
      name: string;
      description?: string;
      price: number;
      currency: string;
      credits: number;
    }>;
  }>> {
    const response = await api.get('/pricing');
    return response.data;
  },
};

// Admin API
export const adminApi = {
  async getTables(): Promise<ApiResponse<Record<string, Array<{ name: string; count: number }>>>> {
    const response = await adminApiInstance.get('/admin/tables');
    return response.data;
  },

  async getTableData(
    tableName: string,
    options: {
      limit?: number;
      page?: number;
      sortKey?: string;
      sortOrder?: 'asc' | 'desc';
      filterKey?: string;
      filterValue?: string;
    } = {}
  ): Promise<ApiResponse<{
    tableName: string;
    items: any[];
    count: number;
    totalCount: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  }>> {
    const params = new URLSearchParams();
    params.set('limit', String(options.limit || 50));
    if (options.page) params.set('page', String(options.page));
    if (options.sortKey) params.set('sortKey', options.sortKey);
    if (options.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options.filterKey) params.set('filterKey', options.filterKey);
    if (options.filterValue) params.set('filterValue', options.filterValue);

    const response = await adminApiInstance.get(`/admin/tables/${tableName}?${params.toString()}`);
    return response.data;
  }
};

// Statistics API
export const statisticsApi = {
  async getDashboardData(days: number = 7): Promise<ApiResponse<{
    messages_over_time: Array<{date: string, count: number}>;
    active_sessions: Array<{date: string, count: number}>;
    top_chatbots: Array<{chatbot_id: string, name: string, message_count: number}>;
    response_times: Array<{date: string, avg_response_time_ms: number}>;
    session_depths: Array<{bucket: string, count: number}>;
    platform_usage: Array<{platform: string, count: number}>;
  }>> {
    const response = await api.get(`/statistics/dashboard?days=${days}`);
    return response.data;
  },

  async getSessionsData(limit: number = 50): Promise<ApiResponse<Array<{
    session_id: string;
    chatbot_id: string;
    platform: string;
    created_at: number;
    last_interaction: number;
    total_messages: number;
    duration_seconds: number;
    status: string;
  }>>> {
    const response = await api.get(`/statistics/sessions?limit=${limit}`);
    return response.data;
  },

  async getConversationData(sessionId: string): Promise<ApiResponse<Array<{
    message_type: string;
    content: string;
    timestamp: string;
    message_sequence: number;
    message_length: number;
    model_id?: string;
    response_time_ms?: number;
  }>>> {
    const response = await api.get(`/statistics/conversation?session_id=${sessionId}`);
    return response.data;
  }
};

// Support API
export const supportApi = {
  async getUploadUrl(filename: string, contentType: string, fileSize: number): Promise<ApiResponse<{
    uploadUrl: string;
    fields: Record<string, string>;
    s3Key: string;
  }>> {
    const response = await api.post('/support/upload-url', {
      filename,
      contentType,
      fileSize
    });
    return response.data;
  },

  async uploadToS3(uploadUrl: string, fields: Record<string, string>, file: File): Promise<void> {
    const formData = new FormData();

    // Add all presigned fields first
    Object.keys(fields).forEach(key => {
      formData.append(key, fields[key]);
    });

    // Add the file last
    formData.append('file', file);

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      // S3 returns 204 No Content on success
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }
    } catch (error: any) {
      // If it's a CORS error but the upload actually succeeded, treat as success
      // This happens because S3 may not return CORS headers on the response
      if (error.message?.includes('CORS') || error.name === 'TypeError') {
        console.log('Upload completed (ignoring CORS response error)');
        return;
      }
      throw error;
    }
  },

  async submitRequest(data: {
    replyEmail: string;
    category: string;
    subject: string;
    message: string;
    attachments: string[];
    userId?: string;
  }): Promise<ApiResponse<{
    ticketId: string;
    message: string;
  }>> {
    const response = await api.post('/support/request', data);
    return response.data;
  }
};

export default api;